import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Business, Delete, Edit } from "@mui/icons-material";
import ContactInfoForm, { ContactInfoData } from "./ContactInfoForm";
import { useAdminBusinesses } from "../../../stores/Admin/locations/locations.queries";
import {
  useCreateBusiness,
  useDeleteBusiness,
  useUpdateBusiness,
} from "../../../stores/Admin/locations/locations.mutations";

interface BusinessFormData {
  id: string;
  name: string;
  bundesland: string;
  creditorId: string;
  taxId: string;
  contactInfo: ContactInfoData;
}

const emptyForm: BusinessFormData = {
  id: "",
  name: "",
  bundesland: "",
  creditorId: "",
  taxId: "",
  contactInfo: {},
};

const BusinessSection: React.FC = () => {
  const { data: businesses = [], isLoading, isError } = useAdminBusinesses();
  const createMutation = useCreateBusiness();
  const updateMutation = useUpdateBusiness();
  const deleteMutation = useDeleteBusiness();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<BusinessFormData>(emptyForm);

  const isEditMode = editId !== null;

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditId(row.id);
    setForm({
      id: row.id,
      name: row.name || "",
      bundesland: row.bundesland || "",
      creditorId: row.creditorId || "",
      taxId: row.taxId || "",
      contactInfo: row.contactInfo || {},
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({
          id: editId!,
          dto: {
            name: form.name,
            bundesland: form.bundesland || undefined,
            creditorId: form.creditorId || undefined,
            taxId: form.taxId || undefined,
            contactInfo: form.contactInfo,
          },
        });
      } else {
        await createMutation.mutateAsync({
          id: form.id,
          name: form.name,
          bundesland: form.bundesland || undefined,
          creditorId: form.creditorId || undefined,
          taxId: form.taxId || undefined,
          contactInfo: form.contactInfo,
        });
      }
      setDialogOpen(false);
    } catch {
      // error handled by mutation onError
    }
  };

  const handleDelete = (row: any) => {
    if (!window.confirm(`Unternehmen "${row.name}" wirklich löschen?`)) return;
    deleteMutation.mutate(row.id);
  };

  const cellSx = { fontWeight: "bold", backgroundColor: "#f5f5f5" };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Business sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6">Unternehmen</Typography>
            <Typography variant="body2" color="text.secondary">
              {businesses.length} Einträge
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={openCreate}
        >
          Neues Unternehmen
        </Button>
      </Box>

      {/* Table */}
      <Paper>
        <TableContainer sx={{ maxHeight: "calc(100vh - 380px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={cellSx}>Name</TableCell>
                <TableCell sx={cellSx}>Bundesland</TableCell>
                <TableCell sx={cellSx}>Gläubiger-ID</TableCell>
                <TableCell sx={cellSx}>Steuer-Nr.</TableCell>
                <TableCell sx={cellSx}>Mandanten</TableCell>
                <TableCell sx={cellSx}>Standorte</TableCell>
                <TableCell sx={cellSx} align="center">
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Alert severity="error">Unternehmen konnten nicht geladen werden</Alert>
                  </TableCell>
                </TableRow>
              ) : businesses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Keine Unternehmen vorhanden
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                businesses.map((row: any) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openEdit(row)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.bundesland || "-"}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {row.creditorId || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {row.taxId || "-"}
                    </TableCell>
                    <TableCell>
                      {row.accounts?.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {row.accounts.slice(0, 3).map((a: any) => (
                            <Chip
                              key={a.id}
                              label={a.name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {row.accounts.length > 3 && (
                            <Chip
                              label={`+${row.accounts.length - 3}`}
                              size="small"
                              color="primary"
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.locationCount || 0}
                        size="small"
                        color={row.locationCount > 0 ? "primary" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(row)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(row)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#e3f2fd" }}>
          {isEditMode
            ? `Unternehmen bearbeiten: ${editId}`
            : "Neues Unternehmen erstellen"}
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          {!isEditMode && (
            <TextField
              fullWidth
              size="small"
              label="ID"
              margin="dense"
              required
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
            />
          )}
          <TextField
            fullWidth
            size="small"
            label="Name"
            margin="dense"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            label="Bundesland"
            margin="dense"
            value={form.bundesland}
            onChange={(e) => setForm({ ...form, bundesland: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            label="Gläubiger-ID (Creditor ID)"
            margin="dense"
            value={form.creditorId}
            onChange={(e) => setForm({ ...form, creditorId: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            label="Steuernummer"
            margin="dense"
            value={form.taxId}
            onChange={(e) => setForm({ ...form, taxId: e.target.value })}
          />

          <Typography
            variant="subtitle1"
            sx={{ mt: 2, mb: 1, fontWeight: 600 }}
          >
            Kontaktinformationen
          </Typography>
          <ContactInfoForm
            value={form.contactInfo}
            onChange={(ci) => setForm({ ...form, contactInfo: ci })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.name || (!isEditMode && !form.id)}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessSection;
